import { Launch } from '@mui/icons-material';
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { SnackbarOpen } from '#app/Action/SnackbarAction';
import { dispatch } from '#app/App';
import ButtonComponent from '#app/Component/ButtonComponent';
import IconAction from '#app/Component/IconAction';
import { getRoutePathname } from '#app/Handler/RouteHandler';
import { generateApiPath, getCollection, writeItem } from '#app/Handler/api';
import { StateType } from '#app/Store';
import useDeposit from '#app/View/InventoryManagement/Inventory/DepositInventory/hooks/useDeposit';
import DepositEntryPoints from '#app/entrypoints/deposit';
import ShopEntryPoints from '#app/entrypoints/shop';
import { InventoryInfo } from '#app/typings/deposit';

type Props = {
  activeFilterStock: () => void;
};

const Confirmation: React.FC<Props> = ({ activeFilterStock }) => {
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const { deposit } = useDeposit();
  const authenticationReducer = useSelector((state: StateType) => state.AuthenticationReducer);
  const navigate = useNavigate();
  const { data, isLoading: loadingData } = useSWR<InventoryInfo[]>(
    authenticationReducer.shopSelected
      ? generateApiPath(DepositEntryPoints.getInventoryInfo, { deposit: deposit as string }, { withStock: true })
      : null,
    getCollection,
    {
      revalidateOnFocus: false,
    },
  );

  const { trigger: validated, isMutating: loading } = useSWRMutation(
    generateApiPath(DepositEntryPoints.inventoryValidate, { deposit: deposit as string }),
    writeItem('POST'),
  );
  const { trigger: notifyManager } = useSWRMutation(
    generateApiPath(ShopEntryPoints.notifyManagerValidatedInventory, {
      id: authenticationReducer.shopSelected as string,
      deposit: deposit as string,
    }),
    writeItem('POST'),
  );

  const handleValidated = async () => {
    setOpen(false);
    await validated().then(async () => {
      dispatch(
        SnackbarOpen({
          text: 'Inventaire Validé.',
          variant: 'success',
        }),
      );

      if (authenticationReducer.shopSelected) {
        await notifyManager().then(() => {
          dispatch(
            SnackbarOpen({
              text: 'Votre responsable a été informé de la validation de l inventaire.',
              variant: 'success',
            }),
          );
        });
      }

      navigate(
        authenticationReducer.shopSelected
          ? getRoutePathname('shop_sales_list_today', { shopId: authenticationReducer.shopSelected })
          : getRoutePathname('inventory_management_inventory_list'),
      );
    });
  };

  return (
    <>
      <Grid container justifyContent="flex-end" className={classes.button}>
        <ButtonComponent
          label="Valider l'inventaire"
          loading={loading}
          disabled={loading}
          onClick={() => setOpen(true)}
        />
      </Grid>
      {open && (
        <Dialog open maxWidth="xl" onClose={() => setOpen(false)}>
          <DialogTitle className={classes.title}>Confirmation</DialogTitle>
          <DialogContent className={classes.content}>
            <p>Confirmez-vous l'inventaire et la mise à jour des stocks ?</p>
            {loadingData && <CircularProgress />}
            {!!data?.length && (
              <Alert
                severity="warning"
                sx={{
                  '& .MuiAlert-message': {
                    flex: 1,
                  },
                }}
              >
                <b>Êtes-vous sûr de continuer sans avoir renseigné ces produits ?</b>
                <ButtonComponent
                  className={classes.buttonFilter}
                  label="Activer le filtre : en stock uniquement"
                  onClick={() => {
                    activeFilterStock();
                    setOpen(false);
                    setTimeout(() => {
                      document
                        .getElementById('search-inventory')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                />
                {data?.map((item) => (
                  <Grid container key={item.id}>
                    <Grid flex={1}>{item.sku}</Grid>
                    <Grid flex={2}>{item.name}</Grid>
                    <Grid flex={1}>{item.flavor}</Grid>
                    <Grid flex={1}>{item.packSize}</Grid>
                    <Grid>
                      <IconAction
                        sx={{ padding: 0 }}
                        onClick={() => {
                          setOpen(false);
                          setTimeout(() => {
                            document
                              .querySelector(`[data-id="${item.id}"]`)
                              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 100);
                        }}
                      >
                        <Launch sx={{ fontSize: 16 }} />
                      </IconAction>
                    </Grid>
                  </Grid>
                ))}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <ButtonComponent color="#5E6E82" label="Annuler" onClick={() => setOpen(false)} disabled={loading} />
            <ButtonComponent label="Valider" loading={loading} onClick={handleValidated} />
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const useStyles = makeStyles({
  title: {
    fontSize: 15,
  },
  content: {
    minWidth: 300,
    width: '90vw',
    maxWidth: 1200,
  },
  button: {
    margin: '15px 0',
  },
  buttonFilter: {
    display: 'block!important',
    margin: '15px 0!important',
  },
});

export default Confirmation;
